import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// This component handles the `#session_id=...` fragment returned by Emergent
// Auth after Google OAuth completes. It runs BEFORE the ProtectedRoute or
// AuthProvider's `/auth/me` check, exchanges the session_id for a JWT cookie
// via POST /api/auth/emergent/session, then navigates to the dashboard.

const API = process.env.REACT_APP_BACKEND_URL;

export default function AuthCallback() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Guard against React StrictMode double-invoke
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    if (!sessionId) {
      navigate('/login?sso_error=missing_session_id', { replace: true });
      return;
    }

    (async () => {
      try {
        await axios.post(
          `${API}/api/auth/emergent/session`,
          {},
          { headers: { 'X-Session-ID': sessionId }, withCredentials: true }
        );
        // Strip the fragment so reloads don't retrigger
        window.history.replaceState(null, '', window.location.pathname);
        await checkAuth();
        navigate('/', { replace: true });
      } catch (e) {
        const detail = e?.response?.data?.detail || e.message;
        setError(typeof detail === 'string' ? detail : 'Google sign-in failed');
        setTimeout(() => {
          navigate(`/login?sso_error=${encodeURIComponent('google_auth_failed')}`, { replace: true });
        }, 1500);
      }
    })();
  }, [navigate, checkAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background" data-testid="auth-callback">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-brand border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-muted-foreground font-bold">
          {error ? 'Sign-in failed' : 'Completing sign-in…'}
        </p>
        {error && <p className="text-xs text-rose-600 mt-2" data-testid="auth-callback-error">{error}</p>}
      </div>
    </div>
  );
}
